import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { I as fileToSrc, o as transDialog, J as restoreUploadedPhotos, g as getLocale, m as getTheme, B as classNames } from './utils2.js';
import { D as DEFAULT_LANG, l as MIME_TYPES } from './const.js';
import { d as defineCustomElement$1 } from './mapy-review-photo-item2.js';

function uploadPhotoFake({ progress, errorRate = 0, }) {
    return new Promise(resolve => {
        let ind = 0;
        const max = 10;
        const failed = Math.random() >= 1 - errorRate;
        let intId = setInterval(() => {
            const percent = Math.floor(ind / max * 100);
            progress(percent);
            ind++;
            // pokud jsme odsouzeni k zahube a je vice jak pulka -> fail
            if (failed && percent > 50) {
                clearInterval(intId);
                intId = null;
                resolve({
                    mediaId: 0,
                    hasError: true,
                });
                return;
            }
            if (ind > max) {
                clearInterval(intId);
                intId = null;
                resolve({
                    mediaId: Math.random() * 10e6 >>> 0,
                    hasError: false,
                });
            }
        }, 100);
    });
}
function uploadPhotoBe({ file, progress, endpoint, apiKey, }) {
    return new Promise(resolve => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", `${endpoint}/mediarpc/v1/reviews`, true);
        if (apiKey) {
            xhr.setRequestHeader("X-Mapy-Api-Key", apiKey);
        }
        xhr.withCredentials = true;
        xhr.responseType = "json";
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                const { mediaId } = xhr.response;
                resolve({
                    mediaId,
                    hasError: false,
                });
            }
            else {
                resolve({
                    mediaId: 0,
                    hasError: true,
                });
            }
        };
        xhr.onerror = () => {
            resolve({
                mediaId: 0,
                hasError: true,
            });
        };
        // behem uploadu updatujeme progress indikator
        xhr.upload.onprogress = (event) => {
            const { loaded, total } = event;
            const percent = Math.min(Math.floor(loaded / total * 100), 100);
            progress(percent);
        };
        xhr.send(file);
    });
}
function uploadPhoto({ file, progress, endpoint, debug = false, errorRate = 0, apiKey, }) {
    return debug
        ? uploadPhotoFake({
            progress,
            errorRate,
        })
        : uploadPhotoBe({
            file,
            progress,
            endpoint,
            apiKey,
        });
}

const mapyReviewPhotosUploadCss = ":host{display:block}.mapy-review-photos-upload{font-family:Arial, Helvetica, sans-serif;display:flex;gap:10px;flex-wrap:wrap}.item-common,mapy-review-photo-item{flex:0 0 72px;height:72px}.file-label{cursor:pointer;background:#fff;border:1px dashed #ccc;border-radius:12px;text-align:center;box-sizing:border-box;overflow:hidden;display:flex;align-items:center;justify-content:center;flex-direction:column;color:#828282;gap:4px}.file-label:hover{color:#1eae00;border-color:#1eae00}.file-label:hover svg{fill:#1eae00}.file-label.no-items{flex:0 1 calc(50% - 8px)}.file-label input{display:none}.file-label svg{width:20px;height:20px;fill:#828282}.mapy-review-photos-upload[data-theme=\"firmy\"] .file-label{border-color:#c00}.mapy-review-photos-upload[data-theme=\"firmy\"] .file-label:hover{color:#c00;border-color:#c00}.mapy-review-photos-upload[data-theme=\"firmy\"] .file-label:hover svg{fill:#c00}";
const MapyReviewPhotosUploadStyle0 = mapyReviewPhotosUploadCss;

var __classPrivateFieldGet = (undefined && undefined.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f)
        throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver))
        throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (undefined && undefined.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m")
        throw new TypeError("Private method is not writable");
    if (kind === "a" && !f)
        throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver))
        throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _MapyReviewPhotosUpload_lang;
const MapyReviewPhotosUpload = /*@__PURE__*/ proxyCustomElement(class MapyReviewPhotosUpload extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.change = createEvent(this, "change", 1);
        this.countChange = createEvent(this, "countChange", 1);
        this.uploadStart = createEvent(this, "uploadStart", 1);
        this.error = createEvent(this, "error", 1);
        this.loading = createEvent(this, "loading", 1);
        this.signal = createEvent(this, "signal", 1);
        _MapyReviewPhotosUpload_lang.set(this, DEFAULT_LANG);
        this.removeItem = (id, logPhotosRemove = false) => {
            const items = this.items.slice();
            const ind = items.findIndex(item => item.id === id);
            const removed = items.splice(ind, 1);
            if (removed.length > 0) {
                this.items = items;
                if (!this.uploading) {
                    this.sendChangeEvent(1);
                    this.countChange.emit(this.items.length);
                }
                logPhotosRemove && this.signal.emit({
                    name: "photos-remove",
                });
            }
        };
        this.updateItem = (id, data) => {
            const items = this.items.slice();
            const curItem = items.filter(item => item.id === id)[0];
            if (curItem) {
                Object.assign(curItem, data);
                this.items = items;
            }
        };
        this.onInputChange = async (event) => {
            const fileInput = event.target;
            const files = Array.from(fileInput.files);
            if (files.length === 0) {
                return;
            }
            // smazeme
            fileInput.value = null;
            this.uploading = true;
            this.loading.emit({
                loading: true,
            });
            let doneCount = 0;
            const errors = [];
            const allPromises = [];
            const photos = [];
            this.uploadStart.emit(files.length);
            /* eslint-disable no-await-in-loop */
            /* eslint-disable no-loop-func */
            for (const file of files) {
                if (this.items.length === this.count) {
                    errors.push("max-count");
                    break;
                }
                if (this.items.filter(item => item.name === file.name).length !== 0) {
                    errors.push("duplicated");
                    break;
                }
                const data = await fileToSrc({
                    file,
                    mimeTypes: MIME_TYPES,
                    minWidth: this.minWidth,
                    minHeight: this.minHeight,
                });
                photos.push({
                    fileName: file.name,
                    error: data.error,
                    width: data.width,
                    height: data.height,
                });
                if (data.hasError) {
                    errors.push(data.error);
                }
                else {
                    const id = Math.random().toString(16).replace("0.", "");
                    this.items = [
                        ...this.items,
                        {
                            id,
                            name: file.name,
                            src: data.src,
                            mediaId: 0,
                            percent: 0,
                            status: "approved",
                        },
                    ];
                    // signal
                    this.countChange.emit(this.items.length);
                    const progress = (percent) => {
                        this.updateItem(id, { percent });
                    };
                    const uploadProm = uploadPhoto({
                        endpoint: this.endpoint,
                        file,
                        progress,
                        debug: this.debug,
                        errorRate: 0.01,
                        apiKey: this.apiKey,
                    });
                    uploadProm.then(uploadData => {
                        if (uploadData.hasError) {
                            errors.push("upload");
                            this.removeItem(id);
                        }
                        else {
                            doneCount++;
                            this.updateItem(id, { mediaId: uploadData.mediaId });
                        }
                    });
                    allPromises.push(uploadProm);
                }
            }
            Promise.all(allPromises).then(() => {
                this.uploading = false;
                this.loading.emit({
                    loading: false,
                });
                this.sendChangeEvent(doneCount);
                if (errors.length > 0) {
                    const errorMessage = this.parseErrorMsg(errors);
                    this.error.emit({
                        error: errorMessage,
                    });
                    this.signal.emit({
                        name: "photos-error",
                        message: errorMessage,
                        photos,
                    });
                }
                else {
                    this.signal.emit({
                        name: "photos-add",
                        photos,
                    });
                }
            });
        };
        this.count = 20;
        this.minWidth = 800;
        this.minHeight = 600;
        this.endpoint = "";
        this.apiKey = "";
        this.debug = false;
        this.photos = [];
        this.service = "mapy";
        this.anonymous = false;
        this.showLabel = true;
        this.items = [];
        this.uploading = false;
    }
    parseErrorMsg(errors) {
        const uniqueErrors = [...new Set(errors)];
        // chybove hlasky
        let error = transDialog(__classPrivateFieldGet(this, _MapyReviewPhotosUpload_lang, "f"), "uploadPhotoErrorCommon");
        if (uniqueErrors.length === 1) {
            switch (uniqueErrors[0]) {
                case "file-type":
                    error = transDialog(__classPrivateFieldGet(this, _MapyReviewPhotosUpload_lang, "f"), "uploadPhotoErrorExtension");
                    break;
                case "dimension":
                    error = transDialog(__classPrivateFieldGet(this, _MapyReviewPhotosUpload_lang, "f"), "uploadPhotoErrorSize");
                    break;
                case "max-count":
                    error = transDialog(__classPrivateFieldGet(this, _MapyReviewPhotosUpload_lang, "f"), "uploadPhotoErrorMaxCount");
                    break;
                case "duplicated":
                    error = transDialog(__classPrivateFieldGet(this, _MapyReviewPhotosUpload_lang, "f"), "imageDuplicateError");
                    break;
            }
        }
        return error;
    }
    sendChangeEvent(changedPhotos) {
        this.change.emit({
            changedPhotos,
            photos: this.items.map(item => ({
                mediaId: item.mediaId,
                src: item.src,
                status: item.status,
            })),
        });
    }
    componentWillLoad() {
        this.items = restoreUploadedPhotos(this.photos);
    }
    componentDidLoad() {
        // mame vlastni fotky -> po nacteni posleme signal o zmene
        const len = this.items.length;
        if (len > 0) {
            this.countChange.emit(len);
        }
    }
    render() {
        const locale = getLocale(this.hostElement);
        __classPrivateFieldSet(this, _MapyReviewPhotosUpload_lang, locale, "f");
        return (h(Host, { key: '83c2daecbfc5580032470dd2262a9c3a55b8a161' }, h("div", { key: '28f9a9d9f8b259bff1be80007ffb3a27492b4a9c', class: "mapy-review-photos-upload", "data-theme": getTheme(this.service) }, (this.showLabel && this.items.length < this.count) && h("label", { key: '4b62523816cf73907c6c7e60d6fda2d5fbb73b07', class: classNames(["file-label", "item-common", this.items.length === 0 ? "no-items" : ""]) }, h("input", { key: '62b59e91f446aa193665e20f504ad41a91aaab24', type: "file", multiple: true, onChange: this.onInputChange }), h("svg", { key: 'c0c5f03d88c98bfd747ee212f9eae9967c25c577', viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", class: "icon icon-image-round" }, h("path", { key: '8c826c41e65d2d78df4c106bdff54ffdfec7078b', "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M8 9C8 7.896 8.896 7 10 7C11.104 7 12 7.896 12 9C12 10.104 11.104 11 10 11C8.896 11 8 10.104 8 9ZM12 4C18.654 4 20 5.346 20 12C20 13.053 19.961 13.959 19.877 14.762L15.641 11.231C15.275 10.928 14.746 10.923 14.375 11.219L10.074 14.66L8.707 13.293C8.355 12.94 7.798 12.9 7.4 13.2L4.231 15.577C4.068 14.599 4 13.428 4 12C4 5.346 5.346 4 12 4ZM12 2C4.215 2 2 4.215 2 12C2 19.785 4.215 22 12 22C19.785 22 22 19.785 22 12C22 4.215 19.785 2 12 2Z" })), h("span", { key: 'a95520a4bfbc0843792c10147ec4b96bce7a9639', class: "caption" }, transDialog(locale, this.items.length === 0 ? "uploadPhoto" : "add"))), this.items.map(item => h("mapy-review-photo-item", { key: item.id, percent: item.percent, src: item.src, rejected: item.status === "rejected", onPhotoDelete: () => this.removeItem(item.id, true), anonymous: this.anonymous })))));
    }
    get hostElement() { return this; }
    static get style() { return MapyReviewPhotosUploadStyle0; }
}, [1, "mapy-review-photos-upload", {
        "count": [514],
        "minWidth": [514, "min-width"],
        "minHeight": [514, "min-height"],
        "endpoint": [513],
        "apiKey": [513, "api-key"],
        "debug": [516],
        "photos": [16],
        "service": [513],
        "anonymous": [516],
        "showLabel": [32],
        "items": [32],
        "uploading": [32]
    }]);
_MapyReviewPhotosUpload_lang = new WeakMap();
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["mapy-review-photos-upload", "mapy-review-photo-item"];
    components.forEach(tagName => { switch (tagName) {
        case "mapy-review-photos-upload":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, MapyReviewPhotosUpload);
            }
            break;
        case "mapy-review-photo-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { MapyReviewPhotosUpload as M, defineCustomElement as d };

//# sourceMappingURL=mapy-review-photos-upload2.js.map